import React, {useCallback, useEffect, useMemo, useRef} from 'react'
import PropTypes from 'prop-types'

/* Components */
import ProductView from '../../../partials/product-view'

/* Hooks */
import {usePDPSearchParams} from '../../../hooks/use-pdp-search-params'
import {useHistory} from 'react-router-dom'
import {trackPsdpColorSelectProductTile} from '../../../tracking/analytics'
import useEffectEvent from '../../../hooks/use-effect-event'
import {GenericModal} from '../../../hooks/use-generic-modal'
import useSelectableSetProduct from '../../../hooks/use-selectable-set-product'
import {getMasterProductId} from '../../../utils/product-utils'

const ProductSetModal = ({isSelectable, product, onClose, ...props}) => {
    const history = useHistory()

    const [allParams, productParams] = usePDPSearchParams(product?.setProductId)
    const [isSelected, setSelected] = useSelectableSetProduct(product)

    const onSetProductSelect = useCallback(() => {
        if (isSelectable) {
            setSelected(true)
        }
    }, [isSelectable, setSelected])

    // compute if different master is loading to show appropriate loading state
    const isMasterLoading = useMemo(() => {
        const wantedPid = productParams.get('pid') || product?.id
        return (
            // when the pid is not the same as the product id
            wantedPid !== product?.id &&
            wantedPid !== getMasterProductId(product) &&
            // and the pid is not in the variants - aka change of material
            !product?.variants?.find((variant) => variant.productId === wantedPid)
        )
    }, [productParams, product])

    const onChangedMaterial = (value) => {
        // delete the selected variation attributes when changing material, as they rarely match, but break useVariationParams
        product?.variationAttributes.forEach((variant) => {
            productParams.delete(variant.id)
        })
        productParams.set('pid', value)
        allParams.set(product?.setProductId, productParams.toString())

        history.replace({search: allParams.toString()})
    }

    const variantChangedRef = useRef(false)

    const onVariantChange = useEffectEvent(() => {
        const changedProduct = product

        // skip the first time
        if (!variantChangedRef.current) {
            variantChangedRef.current = true
            return
        }
        if (changedProduct?.type?.variant) {
            trackPsdpColorSelectProductTile(changedProduct)
        }
    })
    useEffect(() => {
        onVariantChange()
    }, [product?.id, onVariantChange])

    /* If there are no products, don't show component at all */
    if (!product) return null

    return (
        <GenericModal
            variant="setProduct"
            colorScheme="stokkeCore"
            size="lg"
            {...props}
            onClose={onClose}
        >
            <ProductView
                imageSize="sm"
                isMasterProductLoading={isMasterLoading}
                isProductASet
                isSelectable={isSelectable}
                isSetProductSelected={isSelected}
                product={product}
                variant="setProduct"
                onChangedMaterial={onChangedMaterial}
                onClose={onClose}
                onSetProductSelect={onSetProductSelect}
            />
        </GenericModal>
    )
}

ProductSetModal.propTypes = {
    isSelectable: PropTypes.bool,
    product: PropTypes.object,
    onClose: PropTypes.func
}

export default ProductSetModal
